import { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { UsersIcon } from '@heroicons/react/24/outline';
import { AuthContext } from '../../context/AuthContext';
import { apiRequest } from '../../services/api';
import { getSettings, setCoinsEnabled } from '../../services/settings';
import LoadingSpinner from '../../components/global/LoadingSpinner';

function AdminDashboard() {
  const { token } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [coins, setCoins] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Both, so one failing does not blank the other. A settings read that
        // errors leaves the toggle showing false, which matches the fail-closed
        // default on the server rather than guessing.
        const [data, settings] = await Promise.all([
          apiRequest('/api/admin/stats', { token }),
          getSettings(token),
        ]);
        setStats(data);
        setCoins(Boolean(settings.coinsEnabled));
      } catch (err) {
        setError(err.message || 'Failed to load stats');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  const handleToggleCoins = async () => {
    try {
      const next = !coins;
      // State set only AFTER the write lands, never optimistically — a toggle
      // that snaps back is confusing, but one that lies about the live world is
      // worse. Same order SpriteReview uses.
      await setCoinsEnabled(token, next);
      setCoins(next);
      setStatus({
        kind: 'ok',
        text: next
          ? 'Coins are on. They appear in every room now, without anyone reloading.'
          : 'Coins are off. Any on the floor are gone; balances already earned are not.',
      });
    } catch (err) {
      setStatus({ kind: 'error', text: err.message || 'Failed to update setting.' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-10">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage player accounts.
          </p>
        </div>

        {error && (
          <div className="mb-6 rounded-md bg-red-50 dark:bg-red-900/30 p-4">
            <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <StatCard
            label="Registered Players"
            value={stats?.totalUsers ?? 0}
            icon={<UsersIcon className="w-6 h-6" />}
            color="bg-primary"
          />
        </div>

        {/*
          Game settings.
          On the dashboard rather than a page of its own: this is global, has no
          screen it obviously belongs to, and is where an admin already lands.
        */}
        <div className="mb-10 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
            Game settings
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
            Applies to every world, immediately, for everyone already playing.
          </p>

          {status && (
            <div
              className={`mb-4 rounded-md px-4 py-2 text-sm ${
                status.kind === 'error'
                  ? 'bg-red-900/40 text-red-300'
                  : 'bg-emerald-900/40 text-emerald-300'
              }`}
            >
              {status.text}
            </div>
          )}

          <label
            htmlFor="coins-enabled"
            className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
          >
            <input
              id="coins-enabled"
              type="checkbox"
              checked={coins}
              onChange={handleToggleCoins}
              className="mt-0.5 rounded border-gray-400 dark:border-gray-600"
            />
            <span>
              <span className="font-medium text-gray-900 dark:text-white">
                Coins on the floor
              </span>
              <span className="block text-gray-500 dark:text-gray-400">
                Scatters money across every map for players to pick up. A
                placeholder — turning it off removes the coins but never
                the balances people already earned.
              </span>
            </span>
          </label>
        </div>

        {/* Navigation Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link
            to="/admin/users"
            className="block bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Players
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              View registered players and account details.
            </p>
          </Link>
          <Link
            to="/admin/sprites"
            className="block bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Sprite submissions
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Review proposed art, approve or reject, and open submissions to
              all players.
            </p>
          </Link>
          <Link
            to="/admin/worlds"
            className="block bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Worlds
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Every place players can go. Turn one on or off — turning one off
              removes it from the picker and moves anyone inside somewhere else.
            </p>
          </Link>
          {/*
            NPCs are CREATED in game, not here — you walk their route on /play
            and press R at each corner. The description says so, because a page
            called "NPCs" with no create button reads as broken otherwise.
          */}
          <Link
            to="/admin/npcs"
            className="block bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              NPCs
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Edit who your characters are, see the route they walk, and
              schedule when they appear. Create them in game: walk their patrol
              on /play and press R at each corner.
            </p>
          </Link>
          <Link
            to="/admin/businesses"
            className="block bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Opening hours
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              When each place is open, and whether it is shut for now or for
              good. Saved live — there is nothing to publish.
            </p>
          </Link>
          <Link
            to="/sprites"
            className="block bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Sprite editor
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Draw character sheets, preview walk cycles, submit for review.
            </p>
          </Link>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label, value, icon, color,
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700 flex items-center gap-4">
      <div className={`${color} text-white p-3 rounded-lg`}>{icon}</div>
      <div>
        <p className="text-sm text-gray-600 dark:text-gray-400">{label}</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      </div>
    </div>
  );
}

StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  icon: PropTypes.node.isRequired,
  color: PropTypes.string.isRequired,
};

export default AdminDashboard;
